<script setup lang="ts">
import { ref } from 'vue';
import { Header, ButtonType, CustomButton } from '@/components';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import UserListTab from './UserListTab.vue';
import InviteUserListTab from './InviteUserListTab.vue';
import UserInviteDialog from './UserInviteDialog.vue';
import StudyGroupDialog from './groups/StudyGroupDialog.vue';
import { StudyGroupModel } from '@/models/groups';
import { Popup } from '../common/';
import SharableLinkPopupContent from './SharableLinkPopupContent.vue';
import GroupListTab from './groups/GroupListTab.vue';
import { useFormStore } from '@/stores/forms';
import { useStudyStore } from '@/stores/study';
import { useStudyGroupsStore } from '@/stores/studyGroups';

const selectedTab = ref('0');
const openUserInvite = ref<boolean>(false);
const showStudyLinkPopup = ref<boolean>(false);

const linkButtonRef = ref<HTMLElement | null>(null);
const popupReference = ref<HTMLElement | undefined>();
const xValue = ref('0px');
const yValue = ref('0px');

const openUserInviteDialog = () => {
  openUserInvite.value = !openUserInvite.value;
};

const openShareLinkPopup = () => {
  showStudyLinkPopup.value = !showStudyLinkPopup.value;

  // @ts-expect-error: buttonRef could be null in the following statement
  const bounds = linkButtonRef.value?.buttonRef?.getBoundingClientRect();
  if (bounds) {
    setTimeout(() => {
      // @ts-expect-error: target could be null in the following statement
      const popupBounds = popupReference.value?.target?.getBoundingClientRect();
      if (popupBounds) {
        xValue.value = `${bounds.x + bounds.width - popupBounds.width}px`;
        yValue.value = `${bounds.y + bounds.height}px`;
      }
    }, 10);
  }
};

const onTabChange = (tabIndex: string) => {
  selectedTab.value = tabIndex;
};

const outsideCickForOpenShareLink = () => {
  showStudyLinkPopup.value = !showStudyLinkPopup.value;
};

//new group dialog
const groupStore = useStudyGroupsStore();
groupStore.getStudyGroups();

const openGroupDialog = ref<boolean>(false);

const openCloseGroupDialog = (reloadGroups?: boolean) => {
  groupStore.updateGroup(createGroup());
  openGroupDialog.value = !openGroupDialog.value;

  if (reloadGroups === true) {
    groupStore.getStudyGroups();
  }
};

const formStore = useFormStore();
const studyStore = useStudyStore();

const createGroup = () => {
  const group = StudyGroupModel.createEmpty(formStore.formList);
  group.studyIdentifier = studyStore.selectedStudyId as string;
  group.create = true;
  return group;
};
</script>

<template>
  <div class="users">
    <div class="users-wrapper">
      <div class="header">
        <Header title="Manage users">
          <template #headerButtons>
            <div class="header-buttons">
              <template v-if="selectedTab === '0' || selectedTab === '1'">
                <CustomButton text="Invite users"
                  :type="ButtonType.OUTLINED"
                  @onClick="openUserInviteDialog"/>
                <CustomButton text="Share link"
                  :type="ButtonType.POSITIVE"
                  :marginLeft="16"
                  ref="linkButtonRef"
                  @onClick="openShareLinkPopup"/>
              </template>
              <CustomButton text="Create group"
                :type="ButtonType.POSITIVE"
                :marginLeft="16"
                v-if="selectedTab === '2'"
                @onClick="openCloseGroupDialog"/>
            </div>
          </template>
        </Header>
      </div>
      <div class="tabs">
        <Tabs :value="selectedTab" @update:value="onTabChange" :lazy="true">
          <TabList>
            <Tab value="0">TEAM</Tab>
            <Tab value="1">INVITES</Tab>
            <Tab value="2">GROUPS</Tab>
          </TabList>
          <TabPanels>
            <TabPanel value="0">
              <UserListTab />
            </TabPanel>
            <TabPanel value="1">
              <InviteUserListTab @open-invite-dialog="openUserInviteDialog" />
            </TabPanel>
            <TabPanel value="2">
              <GroupListTab @open-close-create-group-dialog="openCloseGroupDialog" />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </div>

  <UserInviteDialog :visible="openUserInvite" @onClose="openUserInviteDialog" v-if="formStore.formList.length > 0"/>

  <StudyGroupDialog :visible="openGroupDialog"
    @onClose="openCloseGroupDialog"/>

  <Popup :visible="showStudyLinkPopup"
    :x="xValue"
    :y="yValue"
    :nestedPopups="true"
    @outsidePopupClick="outsideCickForOpenShareLink"
    ref="popupReference">
    <template #popupContent>
      <SharableLinkPopupContent @onDismiss="outsideCickForOpenShareLink" />
    </template>
  </Popup>
</template>

<style scoped lang="scss">
.users {
  padding: 48px 100px 40px 80px;
  height: 100%;
}
.users-wrapper {
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.header-buttons {
  display: flex;
}
.p-tab {
  width: 100%;
}
.p-tabs {
  height: 100%;
}
.p-tabpanels {
  flex: 1;
  overflow: hidden;
}
:deep(.p-tabpanel) {
  height: 100%;
  overflow: hidden;
}
.tabs {
  flex: 1;
  overflow: hidden;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
</style>
